import { SidebarProvider, Sidebar } from '@cortex/ui';
import * as React from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { RepoCheckStrip } from '../modules/source/index.ts';
import { buildNav, flattenNav } from '../modules/platform/index.ts';
import { parseProjectPath, projectPath } from '../modules/platform/index.ts';
import { displayName, shellRoles, useAuth } from '../modules/identity/index.ts';
import { useTheme } from '../modules/platform/index.ts';
import { useUiView } from '../modules/platform/index.ts';
import { CortexChatPanel as ChatPanel } from '../modules/ai/index.ts';
import { CortexTourHost } from '../modules/platform/index.ts';
import { ChartWindows } from '@cortex/app-shell';
import { CommandPalette } from './CommandPalette.tsx';
import { MobileHeader } from './MobileHeader.tsx';
import { ProjectBranchChip, ProjectSwitcherChip } from './ProjectChip.tsx';
import { SidebarNav } from './Sidebar.tsx';
import { UserMenu } from './UserMenu.tsx';
import { CommitBanner } from './CommitBanner.tsx';
import { CommitViewProvider, useCommitView, useReadOnlyButtons } from '../modules/platform/index.ts';
import { readCache } from '../modules/platform/index.ts';
import { usePrefetchPages } from './prefetch.ts';

/** The application shell: the 250px gradient sidebar and the routed page. */
export function Layout() {
  const { slug = null, branch = null } = useParams();
  return (
    <CommitViewProvider slug={slug} refName={branch}>
      <Shell />
    </CommitViewProvider>
  );
}

/**
 * The routed page at a commit: read-only (write buttons disabled with a tooltip).
 *
 * A page opened again is first shown from the read cache (state/read-cache.ts) at once. When an answer it
 * was shown changes in the moments after, the page is shown again with the new answers, unless the person
 * already started using it (a click or a key in the page): then it keeps what it shows until reopened.
 */
function PageArea() {
  const ref = React.useRef<HTMLDivElement>(null);
  const { atCommit } = useCommitView();
  const { pathname } = useLocation();
  const [round, setRound] = React.useState(0);
  const touched = React.useRef(false);
  useReadOnlyButtons(ref, atCommit);
  React.useLayoutEffect(() => {
    touched.current = false;
    readCache.navigated(pathname);
  }, [pathname]);
  React.useEffect(
    () =>
      readCache.onUpdate(() => {
        if (!touched.current) setRound((r) => r + 1);
      }),
    [],
  );
  const touch = () => {
    touched.current = true;
  };
  return (
    <div
      ref={ref}
      className="flex-1 overflow-auto"
      data-testid="page"
      data-tour="page"
      onPointerDown={touch}
      onKeyDown={touch}
      {...(atCommit ? { 'data-at-commit': '' } : {})}
    >
      <React.Fragment key={round}>
        <Outlet />
      </React.Fragment>
    </div>
  );
}

function Shell() {
  const { slug = null, branch = null } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { simple, setUiView } = useUiView();

  const user = state.status === 'signed-in' ? state.user : null;
  const { fullName, initials } = displayName(user);
  const roles = shellRoles(state).join(',');
  const entries = React.useMemo(
    () => buildNav(simple ? 'simple' : 'advanced', { roles: roles ? roles.split(',') : [] }),
    [simple, roles],
  );
  const rest = parseProjectPath(location.pathname).rest;
  // An open app (apps/:viewName): the chat launcher becomes its companion speed dial.
  const openApp = /^apps\/([^/]+)$/.exec(rest ?? '')?.[1] ?? null;
  // Code City carries its own Assistant pane: the shell's launcher (bottom right) would sit on its controls.
  const codeCity = /^code-city(\/|$)/.test(rest ?? '');
  // Every nav page's code, once the shell is idle: a first click never waits for a chunk.
  usePrefetchPages(entries);

  return (
    <SidebarProvider style={{ '--sidebar-width': '250px' } as React.CSSProperties}>
      <div className="h-screen flex w-full overflow-hidden">
        <Sidebar className="nav-sidebar-bg border-0" data-testid="sidebar">
          <div className="flex flex-col gap-[30px] px-3 pt-[30px] pb-5 h-full overflow-hidden">
            <div className="relative shrink-0 w-[183px] h-[60px]" data-testid="org-logo">
              <Link to="/" className="block w-full h-full">
                <img src="/logo.svg" alt="Logo" className="w-full h-full object-contain" />
              </Link>
            </div>
            {simple && slug ? (
              <div className="flex items-center">
                <ProjectSwitcherChip slug={slug} branch={branch} />
              </div>
            ) : (
              <ProjectBranchChip slug={slug} branch={branch} />
            )}
            <SidebarNav entries={entries} ctx={{ slug, branch, rest }} />
            <UserMenu
              initials={initials}
              fullName={fullName}
              simple={simple}
              theme={theme}
              onToggleView={() => setUiView(simple ? 'advanced' : 'simple')}
              onToggleTheme={toggleTheme}
              onProfile={() => navigate(projectPath('profile', slug, branch))}
              onLogout={() => {
                logout();
                navigate('/login');
              }}
            />
          </div>
        </Sidebar>
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <MobileHeader />
          {slug && <CommitBanner slug={slug} />}
          {slug && branch && (
            <RepoCheckStrip
              slug={slug}
              branch={branch}
              page={decodeURIComponent(location.pathname).split(`/b/${branch}/`)[1] ?? ''}
            />
          )}
          <PageArea />
        </main>
      </div>
      <CommandPalette pages={flattenNav(entries)} slug={slug} branch={branch} />
      {/* The in-app chat, per project (a new project starts a fresh panel). */}
      {/* With an app open, its launcher is the app's companion speed dial (one control, as in old cortex). */}
      {slug && !codeCity && (
        <ChatPanel
          key={slug}
          slug={slug}
          branch={branch ?? 'main'}
          app={openApp ? decodeURIComponent(openApp) : null}
        />
      )}
      {/* The Pipeline engine window (old AssistantChat harness="engine"): no launcher, it opens on a
          chat:open-harness {harness:'engine'} hand-off. */}
      {slug && <ChatPanel key={`${slug}:engine`} slug={slug} branch={branch ?? 'main'} harness="engine" />}
      {/* The spotlight tutorials: the Tutorial button's chooser and every launchTour(). */}
      <CortexTourHost slug={slug} branch={branch} />
      {/* Charts opened from the chat, as floating windows. */}
      <ChartWindows />
    </SidebarProvider>
  );
}
